import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Percent, Shield, UserCog, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

const REP_TYPE_OPTIONS = [
  { value: 'rep1', label: 'Internal Representative (Rep1)', icon: Shield },
  { value: 'rep2', label: 'Senior/Regional Representative (Rep2)', icon: UserCog },
  { value: 'rep3', label: 'Partner Representative (Rep3)', icon: Building2 },
];

const EXPERIENCE_OPTIONS = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid-Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'director', label: 'Director' },
];

export default function EditRepresentativeDialog({ open, onOpenChange, representative, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    repType: '',
    commissionPercentage: 10,
    assignedRegion: '',
    organization: '',
    experienceLevel: 'mid',
    bio: '',
    adminNotes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (representative && open) {
      setFormData({
        firstName: representative.firstName || '',
        lastName: representative.lastName || '',
        phone: representative.phone || '',
        country: representative.country || '',
        repType: representative.role || '',
        commissionPercentage: representative.commissionRate || representative.commissionPercentage || 10,
        assignedRegion: representative.assignedRegion || representative.profile?.assignedRegion || '',
        organization: representative.organization || representative.profile?.organization || '',
        experienceLevel: representative.experienceLevel || representative.profile?.experienceLevel || 'mid',
        bio: representative.profile?.bio || '',
        adminNotes: representative.profile?.adminNotes || '',
      });
      setErrors({});
    }
  }, [representative, open]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (formData.phone && !/^\+?[\d\s-]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Invalid phone format';
    }
    if (formData.commissionPercentage < 0 || formData.commissionPercentage > 100) {
      newErrors.commissionPercentage = 'Commission must be between 0 and 100';
    }
    if (formData.repType === 'rep3' && !formData.organization.trim()) {
      newErrors.organization = 'Organization is required for Partner representatives';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const repId = representative.userId || representative._id;
      const originalCommission = representative.commissionRate || representative.commissionPercentage || 10;

      await adminAPI.updateRepresentative(repId, formData);

      // Update commission separately if changed
      if (formData.commissionPercentage !== originalCommission) {
        await adminAPI.updateRepresentativeCommission(repId, formData.commissionPercentage);
      }

      toast.success('Representative updated successfully');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update representative';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Representative</DialogTitle>
          <DialogDescription>
            Update representative profile and settings for{' '}
            <span className="font-medium text-gray-900">
              {representative?.firstName} {representative?.lastName}
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Email (read-only) */}
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={representative?.email || ''} disabled className="bg-stone-50" />
            <p className="text-xs text-gray-500">Email cannot be changed</p>
          </div>

          {/* Representative Type */}
          <div className="space-y-2">
            <Label>Representative Type</Label>
            <Select
              value={formData.repType}
              onValueChange={(value) => handleChange('repType', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {REP_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-lastName"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-country">Country</Label>
              <Input
                id="edit-country"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </div>
          </div>

          {/* Professional Info */}
          <div className="border-t border-stone-200 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Professional Information</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-region">Assigned Region</Label>
                <Input
                  id="edit-region"
                  value={formData.assignedRegion}
                  onChange={(e) => handleChange('assignedRegion', e.target.value)}
                  placeholder="e.g. South Asia"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-experience">Experience Level</Label>
                <Select
                  value={formData.experienceLevel}
                  onValueChange={(value) => handleChange('experienceLevel', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.repType === 'rep3' && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="edit-org">
                  Organization <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-org"
                  value={formData.organization}
                  onChange={(e) => handleChange('organization', e.target.value)}
                  className={errors.organization ? 'border-red-500' : ''}
                />
                {errors.organization && (
                  <p className="text-sm text-red-500">{errors.organization}</p>
                )}
              </div>
            )}

            <div className="space-y-2 mt-4">
              <Label htmlFor="edit-bio">Bio / Notes</Label>
              <textarea
                id="edit-bio"
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                placeholder="Brief description or notes..."
                rows={2}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* Commission */}
          <div className="space-y-2">
            <Label htmlFor="edit-commission">Commission Rate (%)</Label>
            <div className="relative">
              <Input
                id="edit-commission"
                type="number"
                min="0"
                max="100"
                value={formData.commissionPercentage}
                onChange={(e) => handleChange('commissionPercentage', parseInt(e.target.value) || 0)}
                className={`pr-10 ${errors.commissionPercentage ? 'border-red-500' : ''}`}
              />
              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            {errors.commissionPercentage && (
              <p className="text-sm text-red-500">{errors.commissionPercentage}</p>
            )}
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label htmlFor="edit-adminNotes">Admin Notes (internal)</Label>
            <textarea
              id="edit-adminNotes"
              value={formData.adminNotes}
              onChange={(e) => handleChange('adminNotes', e.target.value)}
              placeholder="Internal notes about this representative..."
              rows={2}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
